import { useTranslation } from 'react-i18next';
import { forceAssert } from '../../common-functions';
import ApplyFilterButton from './common/apply-filter-button.component';
import ClearFilterButton from './common/clear-filter-button.component';
import Calendar from '../../fields/calendar/calendar.component';
import { isDropDownNoSelection, isDropDownResult } from '../../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../../fields/drop-down/drop-down-types';
import DropDown from '../../fields/drop-down/drop-down.component';
import type { JsonData } from '../../generic-types';
import {
  calculateDateRange,
  getCustomDateRangeItem,
  getDateRanges,
} from '../../date-filter-popover/date-range-filter-functions';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangeFilterProps {
  toDate: Date | undefined;
  fromDate: Date | undefined;
  quickDateRange: DropDownResult | DropDownNoSelection | undefined;
  setQuickDateRange: (value: DropDownResult | DropDownNoSelection | undefined) => void;
  setFromDate: (date: Date | undefined) => void;
  setToDate: (date: Date | undefined) => void;
  clearFilter: () => void;
  onFilterApplied?: () => void;
  showApplyButton?: boolean;
  onApplyClick?: () => void;
}

const DateRangeFilterField = ({
  toDate,
  fromDate,
  quickDateRange,
  setQuickDateRange,
  setFromDate,
  setToDate,
  clearFilter,
  onFilterApplied,
  showApplyButton = false,
  onApplyClick,
}: DateRangeFilterProps) => {
  const { t } = useTranslation();

  const dateRanges = getDateRanges();
  const customDateRangeItem = getCustomDateRangeItem();

  const handleQuickDateRangeChange = (value?: DropDownResult | DropDownResult[] | DropDownNoSelection) => {
    if (isDropDownResult(value)) {
      const range = calculateDateRange(value);
      if (range) {
        setFromDate(range.from);
        setToDate(range.to);
        setQuickDateRange(value);
      } else {
        setQuickDateRange(customDateRangeItem);
      }
      if (!showApplyButton) {
        onFilterApplied?.();
      }
    } else if (isDropDownNoSelection(value)) {
      clearFilter();
    }
  };

  const handleFromChange = (from?: Date | Date[] | string | null) => {
    if (!Array.isArray(from)) {
      setFromDate(from as Date);
    }

    if (from || toDate) {
      setQuickDateRange(customDateRangeItem);
    } else {
      setQuickDateRange(undefined);
    }
    if (!showApplyButton) {
      onFilterApplied?.();
    }
  };

  const handleToChange = (to?: Date | Date[] | string | null) => {
    if (!Array.isArray(to)) {
      setToDate(to as Date);
    }

    if (to || fromDate) {
      setQuickDateRange(customDateRangeItem);
    } else {
      setQuickDateRange(undefined);
    }
    if (!showApplyButton) {
      onFilterApplied?.();
    }
  };

  const isApplyDisabled = (!fromDate && !toDate) || (fromDate && toDate && fromDate >= toDate);

  return (
    <>
      <DropDown
        label={t('quick-date-range', 'Quick Date Range')}
        textField="text"
        valueField="value"
        data={forceAssert<JsonData>(dateRanges)}
        value={quickDateRange}
        onChange={handleQuickDateRangeChange}
        selectorStyles="odin-text-sm"
      />
      <div className="odin-flex odin-gap-x-2.5 odin-text-sm odin-text-mono-0">
        <Calendar label={t('from', 'From')} value={fromDate} onChange={handleFromChange} showButtonBar />
        <Calendar label={t('to', 'To')} value={toDate} onChange={handleToChange} showButtonBar />
      </div>
      <div className="odin-flex odin-justify-start odin-items-center odin-gap-2 odin-pt-2">
        {showApplyButton && <ApplyFilterButton onClick={() => onApplyClick?.()} disabled={isApplyDisabled} />}
        <ClearFilterButton onClick={clearFilter} />
      </div>
    </>
  );
};

export default DateRangeFilterField;
